import React, { FC, useCallback, useEffect, useMemo, useState } from 'react';
import { useParams } from 'react-router-dom';
import strings from '../../../../../common/Translation/Translate';
import Config from '../../../../../utils/Config';
import axiosInstance from '../../../../../utils/axios';
import { URLS } from '../../../../../utils/constants/urls';
import { createDropdownData } from '../../../../../utils/validators/HelperFunctions';
import { AlertColor, Box, Grid, Tab, Tabs, Typography } from '@mui/material';
import { TabPanel } from '../../../../../utils/validators/HelperComponents';
import { fetchFromStorage } from '../../../../../utils/storage';
import PageLoader from '../../../../../components/PageLoader';
import Notification from '../../../../../components/Notification';
import ParentOrgGeneralAttributes from './GeneralAttributesTab/ParentOrgGeneralAttributes';
import ParentOrgMedia from './MediaInfoTab/ParentOrgMedia';
import { useSelector } from 'react-redux';
import { languages, Privilege } from '../../../../../utils/redux/reducer/authentication-slice';
import OrgMasterDataList from '../../OrgMasterData/OrgMasterDataList';
import Navigation from '../../../../../utils/hooks/Navigation';
import { routes } from '../../../../../utils/constants/routes';
import { privileges } from '../../../../../utils/constants/privileges';
import { pageTitleSx } from '../../../../../utils/styles/pageHeader';

const AddEditParentOrganization: FC = () => {
  const { id } = useParams() as any;
  const languageList = useSelector(languages) as any;
  const userPrivileges = useSelector(Privilege);

  const [pageResponse, setPageResponse] = useState<any>([]);
  const [masterData, setMasterData] = useState<any>([]);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [tabValue, setTabValue] = useState(parseInt(fetchFromStorage('orgTabValue') || '0'));
  const [message, setMessage] = useState<{
    display: boolean;
    severity: AlertColor | null;
    message: string;
  }>({
    display: false,
    severity: null,
    message: '',
  });

  const closeNotification = (value: boolean) => setMessage(prev => ({ ...prev, display: value }));

  const handleCatch = (error: any) => {
    setMessage({
      display: true,
      severity: 'error',
      message: (error.response.data?.error_description ?? error.response.data.message) ?? error.message,
    });
  };

  const handleTabChange = (_: React.SyntheticEvent, newValue: number) => setTabValue(newValue);

  const getPageData = useCallback(async () => {
    try {
      setIsLoading(true);
      const { data } = await axiosInstance.get(URLS.get_edit_parentOrganizationById(id));
      const container: any = {};
      for (let i = 0; i < languageList?.length; ++i) {
        let values = ['name_' + languageList[i]?.value];
        container[values as any] = data.localizedAttributes[languageList[i]?.value]
          ? data.localizedAttributes[languageList[i]?.value].name
          : '';
      }

      const logo = [];
      const logoObject = {
        preview: Config.BaseURL + data.logoUrl,
        name: data.logoFileName,
        id: data.id,
        type: 'image/png',
      };
      logo.push(logoObject);

      const favicon = [];
      const faviconObject = {
        preview: Config.BaseURL + data.faviconUrl,
        name: data.faviconFileName,
        id: data.id,
        type: 'image/png',
      };
      favicon.push(faviconObject);

      setPageResponse({ ...data, ...container, logo, favicon });
    } catch (error: any) {
      handleCatch(error);
    } finally {
      setIsLoading(false);
    }
  }, [id]);

  useEffect(() => {
    (async () => {
      if (id) {
        await getPageData();
      }
    })();
  }, [getPageData, id]);

  useEffect(() => {
    fetchMasterData();
  }, []);

  const fetchMasterData = async () => {
    const { data } = await axiosInstance.get(URLS.parentOrganizarion_master_data);
    setMasterData(data);
  };

  const dropdowns = useMemo(() => {
    const { eventTypes, venueTypes, locations, layouts } = masterData;
    return {
      eventTypes: createDropdownData(eventTypes, ['id', 'name']),
      venueTypes: createDropdownData(venueTypes, ['id', 'name']),
      locations: createDropdownData(locations, ['id', 'name']),
      layouts: createDropdownData(layouts, ['id', 'name']),
    };
  }, [masterData]);

  return (
    <>
      {isLoading && <PageLoader />}
      {message.display && (
        <Notification
          isOpen={message.display}
          message={message.message}
          severity={message.severity as AlertColor}
          closeNotification={closeNotification}
          autoHideDuration={2000}
        />
      )}
      <Grid container>
        <Grid item xs={10}>
          <Typography sx={pageTitleSx}> {strings.parentOrg_title_update} </Typography>
        </Grid>
        <Grid item textAlign="right" xs={2}>
          <Navigation url={routes.parentOrganization} />
        </Grid>
      </Grid>
      <Box sx={{ p: 0, borderBottom: 1, borderColor: 'divider' }}>
        <Tabs value={tabValue} onChange={handleTabChange} aria-label="basic tabs" data-testId={'tab'}>
          <Tab label={strings.organizations_tabs_general} />
          <Tab disabled={id === undefined} label={strings.organizations_tabs_media_info} />
          {userPrivileges.includes(privileges.view_org_master_data) && (
            <Tab disabled={id === undefined} label={strings.menu_text_master_data} />
          )}
        </Tabs>
      </Box>
      <TabPanel value={tabValue} index={0}>
        <ParentOrgGeneralAttributes
          pageResponse={pageResponse}
          dropdowns={dropdowns}
          languages={languageList}
        />
      </TabPanel>
      <TabPanel value={tabValue} index={1}>
        <ParentOrgMedia
          pageResponse={pageResponse}
          setPageResponse={setPageResponse}
        />
      </TabPanel>
      <TabPanel index={2} value={tabValue}>
        <OrgMasterDataList isParent={true} />
      </TabPanel>
    </>
  );
};

export default AddEditParentOrganization;
